import Axios from 'axios';

import {
	ERRORS_CLEAR,
	TICKET_LOADING_TRUE,
	TICKET_LOADING_FALSE,
	TICKET_CREATE,
	TICKET_CREATE_ERROR,
	ALL_TICKETS_LOADING,
	ALL_TICKETS_GET,
	ALL_TICKETS_ERROR,
	TICKET_REASONS_LOADING,
	TICKET_REASONS_GET,
	TICKET_REASONS_ERROR,
	TICKET_SETTINGS_LOADING,
	TICKET_SETTINGS_GET,
	TICKET_SETTINGS_ERROR,
	MY_TICKETS_LOADING,
	MY_TICKETS_GET,
	MY_TICKETS_ERROR,
	RESOLVED_TICKETS_LOADING,
	RESOLVED_TICKETS_GET,
	RESOLVED_TICKETS_ERROR,
	TICKET_EXISTS_LOADING,
	TICKET_EXISTS_GET,
	TICKET_EXISTS_ERROR,
	TICKET_HISTORY_LOADING,
	TICKET_HISTORY_GET,
	TICKET_HISTORY_ERROR
} from './types';

// Utils
import create_query_params from '../utils/create_query_params';

export const ticket_errors_clear = () => dispatch => {
	dispatch({
		type: ERRORS_CLEAR
	})
}

export const ticket_loading_true = () => dispatch => {
	dispatch({
		type: TICKET_LOADING_TRUE
	})
}

export const ticket_loading_false = () => dispatch => {
	dispatch({
		type: TICKET_LOADING_FALSE
	})
}

// The tickets API returns validation errors as a field-keyed dict
// (e.g. { real_date: "...", description: "..." }) rather than { error: "..." },
// so forward the whole body when present and let the modal render
// field-specific messages instead of "Request failed with status code 400".
const unpackTicketError = (data, fallbackMsg) => {
	let fields = {};
	let msg = fallbackMsg;
	if (data && typeof data === 'object') {
		if (typeof data.error === 'string') {
			msg = data.error;
		} else {
			fields = data;
			const firstMsg = Object.values(data).find((v) => typeof v === 'string');
			if (firstMsg) msg = firstMsg;
		}
	}
	return { msg, fields };
};

// A duplicate is answered with 208 "Already Reported", which is 2xx, so Axios
// resolves it and it would otherwise play the success animation while nothing
// was created. The body is the same field-keyed dict as the 4xx errors; when it
// comes back empty we synthesize the `target` message the modal already knows
// how to translate (CREATE_ERROR_COPY → 'duplicate').
const DUPLICATE_FIELDS = { target: 'there is already an open ticket for that object' };

// `onSuccess` lets the caller refresh whatever it derives from ticket state —
// without it the report banner keeps rendering the snapshot taken on page load,
// so a freshly filed ticket still shows the previous outcome until a reload.
export const create_ticket = (ticket_info, closeModalRef, resetTicketInfo, onSuccess) => dispatch => {
	// console.log(ticket_info)
	dispatch(ticket_loading_true());
	dispatch(ticket_errors_clear());

	let url = process.env.REACT_APP_SERVER_URL + '/api/tickets/create';

	Axios.post(url, ticket_info)
	.then((res) => {
		// console.log(res.data)
		if (res.status === 208) {
			const hasFields = res.data && typeof res.data === 'object' && Object.keys(res.data).length > 0;
			const { msg, fields } = unpackTicketError(
				hasFields ? res.data : DUPLICATE_FIELDS,
				'there is already an open ticket for that object'
			);
			dispatch({
				type: TICKET_CREATE_ERROR,
				payload: { type: 'ticket_create', msg, fields, status: 208 }
			})
			dispatch(ticket_loading_false());
			// The object really is blocked, so re-read the ticket state: the
			// banner was offering the CTA on stale data and has to correct
			// itself behind the open modal.
			if (typeof onSuccess === 'function') onSuccess();
			return;
		}

		dispatch(ticket_loading_false());

		dispatch({
			type: TICKET_CREATE,
			payload: true
		})

		// Refresh right away so the banner is already correct behind the
		// success animation, before the modal closes.
		if (typeof onSuccess === 'function') onSuccess();

		setTimeout(() => {
			closeModalRef.current.click();
		}, 2400);

		setTimeout(() => {
			dispatch({
				type: TICKET_CREATE,
				payload: false
			})
			resetTicketInfo();
		}, 2500);
	}).catch((err) => {
		const { msg, fields } = unpackTicketError(err?.response?.data, err.message);
		dispatch({
			type: TICKET_CREATE_ERROR,
			payload: { type: 'ticket_create', msg, fields, status: err?.response?.status }
		})
		dispatch(ticket_loading_false());
	})
}

// `ticket_types` on a reason is a bitmask of the targets it applies to:
//   1 → image · 2 → payload · 4 → survey response · 0 → any target
// Note the bitmask scale differs from the ticket-type enum (where a survey
// response is 3, not 4) — always compare against the values above.
// Keep only the reasons that apply to the given target. A reason with 0 or
// without a numeric bitmask is treated as applicable everywhere, so neither
// a wildcard nor a missing field hides the whole catalogue.
export const TICKET_TARGET_BIT = { image: 1, payload: 2, response: 4 };

// `ticket_type` on a ticket is an enum, NOT the bitmask above: 1 image ·
// 2 payload · 3 response. They agree on the first two by accident.
export const TICKET_TYPE = { image: 1, payload: 2, response: 3 };

// Keep only the tickets that actually claim the given target. `/mine?payload=X`
// also returns the tickets of that payload's images — there the `payload` field
// is context, not the claimed object — so without this the payload banner would
// surface the outcome of a ticket filed about one of its photos.
export const ticketsForTarget = (tickets, target) => {
	const type = TICKET_TYPE[target];
	const list = Array.isArray(tickets) ? tickets : [];
	if (!type) return list;
	return list.filter((t) => t?.ticket_type === type);
};

// Reads the /exists answer only when it belongs to the object being rendered.
// Returns true/false when the answer is trustworthy, or null when it is unknown
// (never fetched, still in flight, failed, or left over from another object) so
// the caller can fall back instead of treating "unknown" as "free".
export const existsForTarget = (ticketExists, target, targetId) => {
	if (!ticketExists || ticketExists.errored) return null;
	if (ticketExists.target !== target || ticketExists.targetId !== targetId) return null;
	return ticketExists.exists === true;
};

// Reads the /status/history answer only when it belongs to the ticket being
// rendered. Same reason as `existsForTarget`: the slice is global and is not
// cleared on navigation, so without the check the note of a previously visited
// object would be painted under the banner of the next one.
export const historyForTicket = (ticketHistory, ticketId) => {
	if (!ticketHistory || ticketHistory.errored) return [];
	if (!ticketId || ticketHistory.ticketId !== ticketId) return [];
	return Array.isArray(ticketHistory.history) ? ticketHistory.history : [];
};

// The QA note for the status the ticket currently sits in.
//
// Every history entry is a TRANSITION, and the state it moved to is
// `current_status`. An entry has NO `status` field at all, so reading one
// yields undefined and silently kills every note.
//
// Returns null whenever there is nothing worth showing: no transition into that
// status, or one whose comment is absent or blank. That is the common case, not
// the edge one — of the three transitions on the ticket used to verify this,
// only the resolving one carried a comment.
//
// It deliberately does NOT attribute the note to a person. `user` comes back
// unpopulated (a bare `{$oid}`) and `staff` null, and on the verified ticket the
// APPROVED transition carried the ticket author's own id — so rendering a name
// from here could tell the client they wrote QA's note about their own report.
// The UI credits "Backoffice" instead. When backend starts populating this,
// `staff` is the field to read, not `user`, and it still has to be confirmed to
// be the actor before a name reaches the screen.
export const pickStatusNote = (history, status) => {
	if (typeof status !== 'number') return null;
	const list = Array.isArray(history) ? history : [];
	const matches = list.filter((e) => e?.current_status === status);
	if (matches.length === 0) return null;
	// A reopened ticket can pass through the same status twice. The endpoint
	// answers in chronological order today, but picking by date doesn't depend
	// on that holding.
	const latest = matches.reduce((a, b) => ((b?.date?.$date ?? 0) > (a?.date?.$date ?? 0) ? b : a));
	const comment = typeof latest?.comment === 'string' ? latest.comment.trim() : '';
	if (!comment) return null;
	return { comment, date: latest?.date?.$date ?? null };
};

export const reasonsForTarget = (reasons, target) => {
	const bit = TICKET_TARGET_BIT[target];
	const list = Array.isArray(reasons) ? reasons : [];
	if (!bit) return list;
	return list.filter((r) => (
		typeof r?.ticket_types !== 'number'
		|| r.ticket_types === 0
		|| (r.ticket_types & bit) === bit
	));
};

// GET /api/tickets/mine — "the tickets I filed", not "the tickets on this
// object": it is scoped to the token's user. Pass `organization` (the org of
// the claimed object) or the server falls back to the caller's own and answers
// empty for Percepthor staff looking at client data. Naming a foreign org
// without being a platform admin is a 401.
//   { image: <id>, organization: <org>, multiple: '1,2' }    → live on the photo
//   { payload: <id>, organization: <org>, multiple: '1,2' }  → live on the payload
// For "does this object have a live ticket, whoever filed it" use
// `get_ticket_exists` instead; this one is only its fallback.
export const get_my_tickets = (filters) => dispatch => {
	dispatch({ type: MY_TICKETS_LOADING, payload: true });

	let url = process.env.REACT_APP_SERVER_URL + '/api/tickets/mine?';
	url += create_query_params(filters);

	Axios.get(url)
	.then((res) => {
		dispatch({
			type: MY_TICKETS_GET,
			payload: res.data
		})
		dispatch({ type: MY_TICKETS_LOADING, payload: false });
	}).catch((err) => {
		dispatch({
			type: MY_TICKETS_ERROR,
			payload: { type: 'my_tickets', msg: err.message }
		})
		dispatch({ type: MY_TICKETS_LOADING, payload: false });
	})
}

// Same /mine endpoint as get_my_tickets, but stored in its own slice so the
// live (1,2) and resolved (3..8) lookups for a target can coexist. Callers
// pass the target + `multiple: '3,4,5,6,7,8'`; the banner picks the newest.
export const get_resolved_tickets = (filters) => dispatch => {
	dispatch({ type: RESOLVED_TICKETS_LOADING, payload: true });

	let url = process.env.REACT_APP_SERVER_URL + '/api/tickets/mine?';
	url += create_query_params(filters);

	Axios.get(url)
	.then((res) => {
		dispatch({
			type: RESOLVED_TICKETS_GET,
			payload: res.data
		})
		dispatch({ type: RESOLVED_TICKETS_LOADING, payload: false });
	}).catch((err) => {
		dispatch({
			type: RESOLVED_TICKETS_ERROR,
			payload: { type: 'resolved_tickets', msg: err.message }
		})
		dispatch({ type: RESOLVED_TICKETS_LOADING, payload: false });
	})
}

// Effective ticket configuration for the caller: window_days, block_duplicates,
// min/max_description and notify_creator, already resolved through the
// module → organization → code-defaults precedence.
//
// This is NOT the same as `GET /api/tickets/settings`, which returns the
// persisted documents as-is: when an organization has no document — the normal
// case — that endpoint answers {count: 0, settings: []}, leaving no way to know
// the real window or limits. The organization comes from the token, so it must
// not be sent (a non-admin passing `organization` gets a 401).
export const get_ticket_settings = (module) => dispatch => {
	dispatch({ type: TICKET_SETTINGS_LOADING, payload: true });

	let url = process.env.REACT_APP_SERVER_URL + '/api/tickets/settings/mine';
	if (module) url += '?' + create_query_params({ module });

	Axios.get(url)
	.then((res) => {
		dispatch({
			type: TICKET_SETTINGS_GET,
			payload: res.data
		})
		dispatch({ type: TICKET_SETTINGS_LOADING, payload: false });
	}).catch((err) => {
		dispatch({
			type: TICKET_SETTINGS_ERROR,
			payload: { type: 'ticket_settings', msg: err.message }
		})
		dispatch({ type: TICKET_SETTINGS_LOADING, payload: false });
	})
}

// GET /api/tickets/exists — "¿este objeto ya tiene un reporte vivo?", which is
// NOT the question /mine answers ("¿es mío?"). The blocking ticket may have been
// filed by somebody else in the same organization, in which case /mine comes
// back empty and the banner would invite a report that `create` then rejects
// with a 208. Backend guarantees this endpoint and that rejection always agree.
//
// Exactly one of image | payload | response. Live means status 1 (OPEN) or
// 2 (REVIEW) — the same set the duplicate guard evaluates.
//
// The answered target travels with the result so a consumer can tell whether
// what's in the store belongs to the object it is rendering; the slice is
// global and is not cleared on navigation.
export const get_ticket_exists = (target, targetId) => dispatch => {
	if (!target || !targetId) return;

	dispatch({ type: TICKET_EXISTS_LOADING, payload: true });

	let url = process.env.REACT_APP_SERVER_URL + '/api/tickets/exists?';
	url += create_query_params({ [target]: targetId });

	Axios.get(url)
	.then((res) => {
		dispatch({
			type: TICKET_EXISTS_GET,
			payload: {
				target,
				targetId,
				// `code` and `status` come back null when exists is false.
				exists: res.data?.exists === true,
				code: res.data?.code ?? null,
				status: res.data?.status ?? null
			}
		})
		dispatch({ type: TICKET_EXISTS_LOADING, payload: false });
	}).catch((err) => {
		// A failure here must not let the CTA claim the object is free, so the
		// slice keeps the target and reports the failure; consumers fall back to
		// the /mine-derived value.
		dispatch({
			type: TICKET_EXISTS_ERROR,
			payload: { type: 'ticket_exists', msg: err.message, target, targetId }
		})
		dispatch({ type: TICKET_EXISTS_LOADING, payload: false });
	})
}

// GET /api/tickets/:id/status/history — the ticket's transition log. Authorized
// for the client panel (display contract 4.4) and verified to answer 200.
//
// The Display wants exactly one thing out of it: the comment QA left on the
// transition into the ticket's current status, so a resolved banner can explain
// the specific case instead of only the generic state. `pickStatusNote` does
// that reading; everything else in the log is for the ticket detail view.
//
// The answered ticket travels with the result so a consumer can tell whether
// what's in the store belongs to the ticket it is rendering; the slice is global
// and is not cleared on navigation.
export const get_ticket_history = (ticketId) => dispatch => {
	if (!ticketId) return;

	dispatch({ type: TICKET_HISTORY_LOADING, payload: true });

	let url = process.env.REACT_APP_SERVER_URL + `/api/tickets/${ticketId}/status/history`;

	Axios.get(url)
	.then((res) => {
		dispatch({
			type: TICKET_HISTORY_GET,
			payload: {
				ticketId,
				count: res.data?.count ?? 0,
				history: Array.isArray(res.data?.history) ? res.data.history : []
			}
		})
		dispatch({ type: TICKET_HISTORY_LOADING, payload: false });
	}).catch((err) => {
		// The note is an extra: the banner has to look exactly as it does today
		// without it. So a failure keeps the ticket id — to stop the consumer
		// reading the previous ticket's answer — and stays out of the shared
		// `error` slice, which would surface a message for something the user
		// never asked for.
		dispatch({
			type: TICKET_HISTORY_ERROR,
			payload: { type: 'ticket_history', msg: err.message, ticketId }
		})
		dispatch({ type: TICKET_HISTORY_LOADING, payload: false });
	})
}

export const get_ticket_reasons = (organization) => dispatch => {
	dispatch({ type: TICKET_REASONS_LOADING, payload: true });

	let url = process.env.REACT_APP_SERVER_URL + '/api/tickets/reasons?';
	url += create_query_params({ organization, status: 1, skip: 0, limit: 0 });

	Axios.get(url)
	.then((res) => {
		dispatch({
			type: TICKET_REASONS_GET,
			payload: res.data
		})
		dispatch({ type: TICKET_REASONS_LOADING, payload: false });
	}).catch((err) => {
		dispatch({
			type: TICKET_REASONS_ERROR,
			payload: { type: 'ticket_reasons', msg: err.message }
		})
		dispatch({ type: TICKET_REASONS_LOADING, payload: false });
	})
}

export const get_all_tickets = (filters) => dispatch => {
	dispatch({
		type: ALL_TICKETS_LOADING,
		payload: true
	})

	let url = process.env.REACT_APP_SERVER_URL + `/api/tickets?`;
	let query = create_query_params(filters);
	url += query;

	Axios.get(url)
	.then((res) => {
		// console.log(res.data)
		dispatch({
			type: ALL_TICKETS_GET,
			payload: res.data
		})

		dispatch({
			type: ALL_TICKETS_LOADING,
			payload: false
		})
	}).catch((err) => {
		// console.log(err);
		dispatch({
			type: ALL_TICKETS_ERROR,
			payload: { type: 'get_all_tickets', msg: err.message }
		})

		dispatch({
			type: ALL_TICKETS_LOADING,
			payload: false
		})
	})
}
